import {
    serializable,
    DynamicFormControlLayout,
    DynamicInputModel,
    DynamicInputModelConfig,
	MATCH_VISIBLE,
} from "@ng-dynamic-forms/core";
import { Model_WorkflowComponent, CODE_WORKFLOW_COMPONENT, TYPE_DATASOURCE, TYPE_DEFAULT_VALUE, TYPE_WORKFLOW_COMPONENT_OBSERVER, EXPRESSION_FIELD_SCOPE, FONCTION_WORKFLOW_COMPONENT, FONCTION_WORKFLOW_COMPONENT_LABELS } from 'src/app/app.models';
import { COMPONENT_MODE, COMPONENT_UI } from '../workflow-components.models';
import { WorkflowComponentBuilderOptions } from '../workflow-components.service';
import * as moment from 'moment';

export interface DatePickerFormModelConfig extends DynamicInputModelConfig {
	componentMode: COMPONENT_MODE;
}
  
export class DatePickerModel extends DynamicInputModel {
	@serializable() readonly type: string = 'datepicker';
	componentMode: COMPONENT_MODE;
	componentUI: COMPONENT_UI;
	constructor(config: DatePickerFormModelConfig, layout?: DynamicFormControlLayout) {
		super(config, layout);
		this.componentMode = config.componentMode;
		this.componentUI = COMPONENT_UI.MATERIAL;
	}

	static initModel(_component: Model_WorkflowComponent): DatePickerModel {
		const formModel = new DatePickerModel({
			id: _component.ID,
			name: _component.ID,
			label: _component.LABEL,
			value: _component.DEFAULT_VALUE,
			required: _component.IS_REQUIRED,
			readOnly: _component.IS_READONLY,
			hidden: _component.IS_VISIBLE !== undefined ? _component.IS_VISIBLE === false : false,
			validators: _component.CUSTOM_VALIDATORS,
			componentMode: _component.COMPONENT_MODE,
            additional: _component,
			relations: _component.RELATIONS
		});
		return formModel;
	}

	static getProperties(_options?: WorkflowComponentBuilderOptions): Model_WorkflowComponent {
		const workflowSteps = !!_options && !!_options.workflow ? _options.workflow.WORKFLOW_STEPS.map(step => ({ ID: step.NUM_WORKFLOWSTEP, TEXT: `${step.INDEX_WORKFLOWSTEP} - ${step.TITRE}` })) : [];
		const workflowComponents = !!_options && !!_options.workflow ? Array.prototype.concat.apply([], _options.workflow.WORKFLOW_STEPS.filter(step => !!step.COMPONENTS).map(step => step.COMPONENTS.map(component => ({ ID: component.ID, TEXT: `${component.INDEX} - ${component.LABEL} [${component.CODE}]`, NUMWORKFLOW_STEP: step.NUM_WORKFLOWSTEP })))) : [];
		const variablesEnvironnement = !!_options && !!_options.variablesEnvironnement ? _options.variablesEnvironnement.map(v => ({ ID: v.NOM_VARIABLE, TEXT: v.LIBELLE_VARIABLE })) : [];
		
		return {
			ID: 'datepicker',
			CODE: CODE_WORKFLOW_COMPONENT.DATEPICKER,
			ICON: 'calendar-outline',
			LABEL: 'Date Picker',
			PROPERTIES: [
				{ ID: 'GUID', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'GUID', IS_VISIBLE: false },
				{ ID: 'INDEX', CODE: CODE_WORKFLOW_COMPONENT.NUMERIC_TEXTBOX, LABEL: 'Index', DEFAULT_VALUE: 1 },
				{ ID: 'ID', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Identifiant', IS_REQUIRED: true, CUSTOM_VALIDATORS: [{ VALIDATOR_NAME: 'no_whitespace' }] },
				{ ID: 'LABEL', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Libellé', IS_REQUIRED: true },
				{ ID: 'IS_VISIBLE', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Est Visible ?', DEFAULT_VALUE: true },
				{ ID: 'IS_REQUIRED', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Est Requis ?' },
				{ ID: 'IS_READONLY', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Lecture Seule ?' },
				{ ID: 'TYPE_DEFAULT_VALUE', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Type Valeur', DEFAULT_VALUE: TYPE_DEFAULT_VALUE.CONSTANT, IS_REQUIRED: true, 
					UIC_PARAMS: {
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [
								{ ID: TYPE_DEFAULT_VALUE.CONSTANT, TEXT: 'Constante' }, 
								{ ID: TYPE_DEFAULT_VALUE.COMPUTED, TEXT: 'Calculé' },
								{ ID: TYPE_DEFAULT_VALUE.ENVIRONMENT_VARIABLE, TEXT: 'Variable d\'environnement' },
								{ ID: TYPE_DEFAULT_VALUE.WORKFLOW_FIELD, TEXT: 'Champ du workflow' },
								{ ID: TYPE_DEFAULT_VALUE.FONCTION, TEXT: 'Fonction' },
							],
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					}
				},
				{ ID: 'DEFAULT_VALUE', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Valeur par défaut', RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'TYPE_DEFAULT_VALUE', value: TYPE_DEFAULT_VALUE.CONSTANT }] }] },
				{ 
					ID: 'DEFAULT_VALUE_EXPRESSION', 
					CODE: CODE_WORKFLOW_COMPONENT.RICH_AUTOCOMPLETE, 
					LABEL: 'Expression à évaluer', 
					RELATIONS: [
						{ 
							match: MATCH_VISIBLE, 
							when: [{ id: 'TYPE_DEFAULT_VALUE', value: TYPE_DEFAULT_VALUE.COMPUTED }] 
						}
					] 
				},
				{ 
					ID: 'DEFAULT_VALUE', 
					CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, 
					LABEL: 'Variable d\'environnement', 
					UIC_PARAMS: {
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: variablesEnvironnement,
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					},
					RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'TYPE_DEFAULT_VALUE', value: TYPE_DEFAULT_VALUE.ENVIRONMENT_VARIABLE }] }] 
				},
				{ 
					ID: 'DEFAULT_VALUE_NUMWORKFLOW_STEP', 
					CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, 
					LABEL: 'Etape workflow', 
					IS_REQUIRED: true,
					UIC_PARAMS: {
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: workflowSteps,
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					},
					RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'TYPE_DEFAULT_VALUE', value: TYPE_DEFAULT_VALUE.WORKFLOW_FIELD }] }] 
				},
				{ 
					ID: 'DEFAULT_VALUE_WORKFLOW_COMPONENT_ID', 
					CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, 
					LABEL: 'Champ workflow', 
					IS_REQUIRED: true,
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'DEFAULT_VALUE_NUMWORKFLOW_STEP', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.FILTER_DATASOURCE, params: 'NUMWORKFLOW_STEP' }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: workflowComponents,
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					},
					RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'TYPE_DEFAULT_VALUE', value: TYPE_DEFAULT_VALUE.WORKFLOW_FIELD }] }] 
				},
				{ 
					ID: 'DEFAULT_VALUE_WORKFLOW_COMPONENT_PROPERTY', 
					CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, 
					LABEL: 'Propriété', 
					IS_VISIBLE: false,
					CONDITIONS_VISIBILITY: { condition: 'and', rules: [
						{ field: 'DEFAULT_VALUE_WORKFLOW_COMPONENT_ID', operator: 'eq', scope: EXPRESSION_FIELD_SCOPE.WORKFLOW_COMPONENT_METADATA, value: CODE_WORKFLOW_COMPONENT.DROPDOWN, datasource_column: 'CODE' },
						{ field: 'TYPE_DEFAULT_VALUE', operator: 'eq', scope: EXPRESSION_FIELD_SCOPE.CONSTANT, value: TYPE_DEFAULT_VALUE.WORKFLOW_FIELD }
					]},
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'DEFAULT_VALUE_WORKFLOW_COMPONENT_ID', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.ASSIGN_WORKFLOW_COMPONENT_DATAS, params: 'DS_COLUMNS' }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					},
				},
				{ 
					ID: 'TYPE_DEFAULT_VALUE_FUNCTION_ID', 
					CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, 
					LABEL: 'Fonction prédéfinie', 
					DEFAULT_VALUE: TYPE_DEFAULT_VALUE.CONSTANT, 
					IS_REQUIRED: true, 
					UIC_PARAMS: {
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [{ mVALUE_MEMBER: FONCTION_WORKFLOW_COMPONENT.DATE_EXECUTION_WORKFLOW, mDISPLAY_MEMBER: FONCTION_WORKFLOW_COMPONENT_LABELS[FONCTION_WORKFLOW_COMPONENT.DATE_EXECUTION_WORKFLOW] }],
							DATASOURCE_VALUE_FIELD: 'mVALUE_MEMBER',
							DATASOURCE_DISPLAY_FIELD: 'mDISPLAY_MEMBER'
						}
					},
					RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'TYPE_DEFAULT_VALUE', value: TYPE_DEFAULT_VALUE.FONCTION }] }] 
				},
				{ ID: 'DATE_FORMAT', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Format Date', IS_REQUIRED: true, DEFAULT_VALUE: 'YYYY-MM-DD HH:mm' },
			]
		};
	}
}
  